'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string | string[];
}

const StandardsFAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How does AI Change Leaders relate to ISO/IEC 42001?',
      answer: [
        'AI Change Leaders complements, but does not replace, ISO/IEC 42001.',
        'ISO/IEC 42001 defines requirements for AI management systems—establishing governance structures, risk management processes, and operational controls. It specifies what organisations must implement to manage AI responsibly.',
        'AI Change Leaders focuses on organisational leadership, adoption, and change capability—the human dimensions required to implement governance standards effectively in practice. We assess and recognise maturity in the leadership capabilities that enable standards like ISO/IEC 42001 to be translated from requirements into operational reality.',
        'This is a complementary relationship: standards define the framework; AI Change Leadership builds the organisational capability to implement it successfully.',
      ],
    },
    {
      question: 'Is AI Change Leaders certified against ISO/IEC 42001?',
      answer: [
        'No. AI Change Leaders is not certified against ISO/IEC 42001, nor do we certify or audit organisations against it.',
        'We are an independent, practitioner-led body focused on defining best practice in AI leadership and organisational adoption. Our role is to assess, benchmark, and recognise organisations that demonstrate maturity in the leadership and change capabilities required to scale AI responsibly—including the capability to implement governance standards effectively.',
      ],
    },
    {
      question: 'Does the AI Change Leader Standard address the same requirements as ISO/IEC 42001?',
      answer: [
        'No. The AI Change Leader Standard and ISO/IEC 42001 address different but complementary aspects of AI adoption.',
        'ISO/IEC 42001 focuses on AI management systems—governance structures, risk management, and operational controls.',
        'The AI Change Leader Standard focuses on leadership alignment, operating model adaptation, workforce enablement, adoption intelligence, and continuous evolution—the organisational capabilities required to lead AI-driven transformation at scale.',
        'Organisations with mature AI Change Leadership capabilities are better positioned to implement governance standards like ISO/IEC 42001 successfully.',
      ],
    },
    {
      question: 'Can AI Change Leaders help us achieve ISO/IEC 42001 compliance?',
      answer: [
        'AI Change Leaders does not provide ISO/IEC 42001 compliance services, certification, or audit support.',
        'We assess and recognise organisational maturity in the leadership and change capabilities that enable effective implementation of governance frameworks. Organisations that develop these capabilities are better equipped to implement standards like ISO/IEC 42001—but compliance itself is outside our scope.',
        'Implementation may be led by internal teams or specialist partners. We sit above delivery, focusing on assessment, benchmarking, and recognition of leadership maturity.',
      ],
    },
    {
      question: 'Why is AI Change Leadership important for organisations adopting governance standards?',
      answer: [
        'Governance standards define what must be done. AI Change Leadership defines how organisations build the capability to do it—at the pace AI evolves.',
        'Many organisations struggle to implement governance frameworks not because they lack technical understanding, but because they lack leadership alignment, workforce readiness, and the organisational change capability required to embed new processes and accountability structures.',
        'AI Change Leadership addresses these gaps—enabling organisations to translate governance requirements into operational practice, sustain transformation momentum, and adapt as AI capabilities advance.',
      ],
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-headline text-[#0A2540] mb-6">
              Standards & Governance
            </h2>
            <p className="text-lg md:text-xl text-gray-700 font-body leading-relaxed">
              Understanding AI Change Leaders' relationship to AI governance standards and frameworks.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-2 border-gray-200 bg-white">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  aria-expanded={openIndex === index}
                >
                  <span className="text-xl font-bold font-headline text-[#0A2540] pr-8">
                    {faq.question}
                  </span>
                  <Icon
                    name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    variant="outline"
                    className="flex-shrink-0 text-[#0A2540]"
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="pt-4 border-t-2 border-gray-100">
                      {Array.isArray(faq.answer) ? (
                        <div className="space-y-4">
                          {faq.answer.map((paragraph, pIndex) => (
                            <p key={pIndex} className="text-base text-gray-700 font-body leading-relaxed">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-base text-gray-700 font-body leading-relaxed">{faq.answer}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-50 border-2 border-gray-200 p-10">
            <h3 className="text-2xl font-bold font-headline text-[#0A2540] mb-4">
              Our Position
            </h3>
            <p className="text-lg text-gray-700 font-body leading-relaxed mb-4">
              AI Change Leaders is an independent, practitioner-led body focused on defining, assessing, and recognising best practice in AI leadership and organisational adoption.
            </p>
            <p className="text-lg text-gray-700 font-body leading-relaxed">
              We respect the boundaries of formal standards bodies and do not position ourselves as a regulatory or certification authority. Our focus is on the leadership and change capabilities that enable organisations to implement governance frameworks effectively—a distinct but complementary contribution to responsible AI adoption.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StandardsFAQSection;